import {
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Select,
  Typography,
} from 'antd';
import cn from 'classnames';
import moment from 'moment';
import { type FC } from 'react';

import { Phone } from '@app/components';
import { Avatar } from '@app/components/Avatar';
import { ClientAdditionalFields } from '@app/components/EditClient/components/ClientAdditionalFields';
import { ClientSocialAccounts } from '@app/components/EditClient/components/ClientSocialAccounts';
import { fields } from '@app/components/EditClient/data/constants';
import {
  useClientData,
  useClientDataEffects,
} from '@app/components/EditClient/hooks';
import type { EditClientProps } from '@app/components/EditClient/types';
import { FormItemField, SnilsInput } from '@app/components/FormFields';
import { ClientDuplicateModal } from '@app/components/Modals/ClientDuplicateModal';
import { phoneValidateHandler } from '@app/components/Phone/utils';
import { DictionarySelect } from '@app/components/RecordModal/components/MedicalCard/components';
import { MedicalDictionaryType } from '@app/components/RecordModal/components/MedicalCard/data';
import { TagSelect } from '@app/components/TagsSelect';
import {
  DATE_PICKER_FORMAT,
  GENDER,
  OFF,
  ONE_TIME_CODE,
  rangePickerLocale,
  UNKNOWN_GENDER_ID,
} from '@app/constants';

import '@app/components/EditClient/styles/ClientData.desktop.scss';

const PF = 'client-data';
const { Text, Title } = Typography;
const { Item } = Form;
const { TextArea } = Input;

export const EditClient: FC<EditClientProps> = (props) => {
  const {
    form,
    onFinish,
    languagesOptions,
    tagOptions,
    initForm,
    handleConfirm,
    isOpen,
    hasChanges,
    isInitialized,
    checkUnsavedData,
    fallbackName,
    photoUrl,
    onConfirm,
    avatarDropdownItems,
    isLoading,
    setPhonesValidation,
    phonesValidation,
    IsTagsChange,
    duplicateCheckResult,
    closeDuplicateModalHandler,
    openExistingClientHandler,
    mergeClientsHandler,
    importantClientFieldDefs,
    regularClientFieldDefs,
    refreshClientFieldDefs,
  } = useClientData(props);

  useClientDataEffects({
    initForm,
    handleConfirm,
    isOpen,
    hasChanges,
    isInitialized,
    checkUnsavedData,
  });

  const { clientId, onCancel, titleExtra } = props;

  return (
    <div className={`${PF}_wrapper`}>
      <div className={`${PF}_header`}>
        <Title className={`${PF}_title`} level={4} data-testid="client-data">
          Данные клиента
        </Title>
        {titleExtra}
      </div>
      <div className={`${PF}_container`}>
        <div className={`${PF}_content`}>
          <Form
            form={form}
            onFinish={onFinish}
            className={`${PF}_form`}
            clearOnDestroy
            autoComplete={OFF}
            layout="vertical"
            initialValues={{
              Gender: UNKNOWN_GENDER_ID,
            }}
            scrollToFirstError={true}
            onFinishFailed={() => {
              phoneValidateHandler(form, phonesValidation.phone1, 'Phone1');
            }}
          >
            <div className={`${PF}_fields`}>
              {fields.map(
                ({
                  label,
                  rules,
                  required,
                  name,
                  labelTestId,
                  inputTestId,
                  maxLength,
                }) => (
                  <div key={name} className={`${PF}_fields_item`}>
                    <Text
                      data-testid={labelTestId}
                      className={cn(`${PF}_fields_label`, {
                        'label-require': required,
                      })}
                    >
                      {label}
                    </Text>
                    <Item
                      name={name}
                      rules={rules}
                      className={`${PF}_fields_input`}
                    >
                      <Input
                        data-testid={inputTestId}
                        maxLength={maxLength}
                        autoComplete={ONE_TIME_CODE}
                        size="large"
                        disabled={isLoading}
                      />
                    </Item>
                  </div>
                )
              )}
              <div className={`${PF}_fields_item phone-info`}>
                <Text
                  data-testid="main-phone"
                  className={cn(`${PF}_fields_label`, 'label-require')}
                >
                  Основной телефон
                </Text>
                <div className="phone-info_control">
                  <Item name="Phone1" style={{ width: '100%' }}>
                    <Phone
                      name="Phone1"
                      required
                      disabled={isLoading}
                      onValidate={(validation) =>
                        setPhonesValidation((prev) => ({
                          ...prev,
                          phone1: validation,
                        }))
                      }
                    />
                  </Item>
                  <Item name="IsPhoneVerified" valuePropName="checked" noStyle>
                    <Checkbox
                      className="phone-verified-checkbox"
                      disabled={isLoading}
                      data-testid="phone-confirmed"
                    >
                      <Text type="secondary">Телефон подтверждён</Text>
                    </Checkbox>
                  </Item>
                </div>
              </div>

              <ClientAdditionalFields
                variant="important"
                clientId={clientId}
                fieldDefs={importantClientFieldDefs}
                isLoading={isLoading}
                onFieldsChanged={refreshClientFieldDefs}
              />

              <SnilsInput
                fieldName="Snils"
                label="СНИЛС пациента (11 цифр)"
                disabled={isLoading}
                isStrongTitle={false}
              />

              <DictionarySelect
                disabled={isLoading}
                type={MedicalDictionaryType.IdentityDocuments}
                title="Удостоверение личности"
                name={'IdentityDocumentType'}
                placeholder=""
                isStatic
                isStrongTitle={false}
              />

              <FormItemField
                label="Номер документа"
                fieldName="IdentityDocumentNumber"
                isStrongTitle={false}
              >
                <Input size="large" maxLength={100} disabled={isLoading} />
              </FormItemField>

              <FormItemField
                label="Адрес регистрации"
                fieldName="RegistrationAddress"
                isStrongTitle={false}
              >
                <Input size="large" maxLength={100} disabled={isLoading} />
              </FormItemField>

              <div className={`${PF}_fields_item`}>
                <Text
                  data-testid="additional-phone"
                  className={cn(`${PF}_fields_label`)}
                >
                  Дополнительный телефон
                </Text>
                <Item name="Phone2" style={{ width: '100%' }}>
                  <Phone
                    name="Phone2"
                    disabled={isLoading}
                    onValidate={(validation) =>
                      setPhonesValidation((prev) => ({
                        ...prev,
                        phone2: validation,
                      }))
                    }
                  />
                </Item>
              </div>
              <div className={`${PF}_fields_item`}>
                <Text data-testid="gender" className={cn(`${PF}_fields_label`)}>
                  Пол
                </Text>
                <Item name="Gender" className={`${PF}_fields_input`}>
                  <Select
                    data-testid="gender-select"
                    className={`${PF}_fields_select`}
                    size="large"
                    options={GENDER}
                    disabled={isLoading}
                  />
                </Item>
              </div>
              <div className={`${PF}_fields_item`}>
                <Text
                  data-testid="language"
                  className={cn(`${PF}_fields_label`)}
                >
                  Язык для общения
                </Text>
                <Item name="Language" className={`${PF}_fields_input`}>
                  <Select
                    data-testid="language-select"
                    className={`${PF}_fields_select`}
                    size="large"
                    disabled={isLoading}
                    options={languagesOptions}
                    allowClear
                  />
                </Item>
              </div>
              <div className={`${PF}_fields_item`}>
                <Text className={cn(`${PF}_fields_label`)} data-testid="tag">
                  Тег
                </Text>
                <Item name="Tags" className={`${PF}_fields_input`}>
                  <TagSelect
                    className={`${PF}_fields_select`}
                    placeholder="Выберите теги"
                    dataTestId="tag-select"
                    tagTestId="name-selected-tag"
                    size="large"
                    showSearch
                    disabled={isLoading || !IsTagsChange}
                    options={tagOptions}
                  />
                </Item>
              </div>
              <div className={`${PF}_fields_item`}>
                <Text
                  data-testid="birth-date"
                  className={cn(`${PF}_fields_label`)}
                >
                  Дата рождения
                </Text>
                <Item name="DateBirth" className={`${PF}_fields_input`}>
                  <DatePicker
                    data-testid="birth-date-select"
                    autoComplete={ONE_TIME_CODE}
                    locale={rangePickerLocale}
                    className={`${PF}_fields_select`}
                    classNames={{ popup: { root: 'record-modal-calendar' } }}
                    size="large"
                    format={DATE_PICKER_FORMAT}
                    disabled={isLoading}
                    disabledDate={(current) =>
                      current.unix() >= moment().startOf('day').unix()
                    }
                  />
                </Item>
              </div>
              <div className={`${PF}_fields_item ${PF}_fields_item--textarea`}>
                <Text className={cn(`${PF}_fields_label`)} data-testid="note">
                  Примечание
                </Text>
                <Item
                  name="Comment"
                  className={`${PF}_fields_textarea`}
                  noStyle
                >
                  <TextArea
                    rows={4}
                    maxLength={600}
                    disabled={isLoading}
                    data-testid="note-input"
                  />
                </Item>
              </div>
              <div
                className={`${PF}_fields_item ${PF}_fields_item--textarea organization-comment`}
              >
                <Text className={cn(`${PF}_fields_label`)}>
                  Комментарий к
                  <br />
                  организации
                </Text>
                {/*
                  Без `noStyle`: это последнее штатное поле формы, за ним идут
                  дополнительные поля клиента. Отступ между полями страницы
                  даёт сам Form.Item (margin-bottom), а `_fields_item` своих
                  вертикальных отступов не имеет — с `noStyle` textarea
                  вплотную прилипала к первому дополнительному полю.
                */}
                <Item name="CompanyName" className={`${PF}_fields_textarea`}>
                  <TextArea rows={4} maxLength={1000} />
                </Item>
              </div>
              <ClientAdditionalFields
                variant="regular"
                clientId={clientId}
                fieldDefs={regularClientFieldDefs}
                isLoading={isLoading}
                onFieldsChanged={refreshClientFieldDefs}
              />
            </div>
            <div className={`${PF}_avatar`}>
              <Item name="Photo" noStyle>
                <Avatar src={photoUrl} nameFirst={fallbackName} />
              </Item>
              <Dropdown menu={{ items: avatarDropdownItems }}>
                <Button
                  data-testid="change-avatar"
                  type="link"
                  disabled={isLoading}
                >
                  Изменить
                </Button>
              </Dropdown>
              <ClientSocialAccounts disabled={isLoading} />
            </div>
          </Form>
        </div>
      </div>
      <div className={`${PF}_footer`}>
        <Button
          type="primary"
          size="large"
          className={`${PF}_footer_button`}
          onClick={onConfirm || form.submit}
          disabled={isLoading}
          data-testid="save"
        >
          Сохранить
        </Button>
        <Button
          type="default"
          size="large"
          className={`${PF}_footer_button`}
          onClick={onCancel}
          disabled={isLoading}
          data-testid="cancel"
        >
          Отмена
        </Button>
      </div>
      {duplicateCheckResult && (
        <ClientDuplicateModal
          open={true}
          phone={duplicateCheckResult.CheckClientPhone ?? ''}
          isMergePossible={duplicateCheckResult.IsMergePossible}
          onCancel={closeDuplicateModalHandler}
          onOpenClient={openExistingClientHandler}
          onMerge={mergeClientsHandler}
        />
      )}
    </div>
  );
};
